import { z } from 'zod';
import { request, useMutation, useQueryClient } from '@applyboard/ui-utils';
import { NewVasError } from '../../common/errors/NewVasError';

const NudgeRuleSchema = z.object({
  data: z.object({
    type: z.literal('nudge_rules'),
    id: z.string(),
    attributes: z.object({
      user_id: z.string(),
      state: z.string(),
      rule_name: z.string(),
    }),
  }),
});

export type NudgeRule = z.infer<typeof NudgeRuleSchema>;

const NOTIFICATION_NUDGE_RULES_V1_ROOT_URL = '/api/vas/nudge_rules/v1/';

export function usePauseNudgeRule() {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isLoading, isError } = useMutation(async (ruleName: string) =>
    request(NOTIFICATION_NUDGE_RULES_V1_ROOT_URL, {
      method: 'PUT',
      allowAnonymous: false,
      schema: NudgeRuleSchema,
      body: {
        rule_name: ruleName,
        state: 'PAUSED',
      },
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new NewVasError(jsonMessage, 'Pause nudge rule failed');
      },
    }),
  );

  const pauseNudgeRule = (
    ruleName: string,
    onSuccess?: (response: NudgeRule) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(ruleName, {
      onSuccess: async response => {
        await queryClient.invalidateQueries(['useGetNotification', { pkg: 'ab-vas-gic' }]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };

  const pauseNudgeRuleAsync = async (ruleName: string) => {
    const response = await mutateAsync(ruleName);
    queryClient.invalidateQueries(['useGetNotification', { pkg: 'ab-vas-gic' }]);
    return response;
  };

  return {
    pauseNudgeRule: pauseNudgeRule,
    pauseNudgeRuleAsync: pauseNudgeRuleAsync,
    isDismissingNotification: isLoading,
    isDimissingNotificationError: isError,
  };
}
